'use client'

import { FormEvent, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Phone } from 'lucide-react'

const reasons = [
  'Solicitar el catálogo para instituciones',
  'Cotizar materiales específicos',
  'Equipar una institución o nuevos ambientes',
  'Incorporar, renovar o reponer materiales',
]

export function InstitutionalContactForm() {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    setLoading(true)
    setSuccess(false)
    setError('')

    const form = event.currentTarget
    const formData = new FormData(form)

    const payload = {
      type: 'institutional',
      name: String(formData.get('name') || '').trim(),
      institution: String(formData.get('institution') || '').trim(),
      email: String(formData.get('email') || '').trim(),
      whatsapp: String(formData.get('whatsapp') || '').trim(),
      location: String(formData.get('location') || '').trim(),
      reason: String(formData.get('reason') || '').trim(),
      message: String(formData.get('message') || '').trim(),
    }

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data?.error || 'No pudimos enviar la consulta')
      }

      setSuccess(true)
      form.reset()
    } catch (err) {
      console.error('Error enviando consulta institucional:', err)
      setError(
        'No pudimos enviar tu consulta. Por favor, intentá nuevamente o escribinos por WhatsApp.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className='bg-white border border-white/20 rounded-md p-6 md:p-8 lg:p-10'>
        <form onSubmit={handleSubmit} className='space-y-5'>
          <div>
            <label
              htmlFor='institutional-name'
              className='block font-sans text-sm font-medium text-[#7D744F] mb-2'
            >
              Nombre y apellido *
            </label>

            <input
              id='institutional-name'
              name='name'
              type='text'
              required
              autoComplete='name'
              className='w-full rounded-md border border-[#DDD8CC] bg-white px-4 py-3 text-[#4F4938] outline-none transition focus:border-[#00D866] focus:ring-2 focus:ring-[#00D866]/15'
            />
          </div>

          <div>
            <label
              htmlFor='institutional-institution'
              className='block font-sans text-sm font-medium text-[#7D744F] mb-2'
            >
              Institución *
            </label>

            <input
              id='institutional-institution'
              name='institution'
              type='text'
              required
              autoComplete='organization'
              className='w-full rounded-md border border-[#DDD8CC] bg-white px-4 py-3 text-[#4F4938] outline-none transition focus:border-[#00D866] focus:ring-2 focus:ring-[#00D866]/15'
            />
          </div>

          <div className='grid md:grid-cols-2 gap-5'>
            <div>
              <label
                htmlFor='institutional-email'
                className='block font-sans text-sm font-medium text-[#7D744F] mb-2'
              >
                Email *
              </label>

              <input
                id='institutional-email'
                name='email'
                type='email'
                required
                autoComplete='email'
                className='w-full rounded-md border border-[#DDD8CC] bg-white px-4 py-3 text-[#4F4938] outline-none transition focus:border-[#00D866] focus:ring-2 focus:ring-[#00D866]/15'
              />
            </div>

            <div>
              <label
                htmlFor='institutional-whatsapp'
                className='block font-sans text-sm font-medium text-[#7D744F] mb-2'
              >
                WhatsApp *
              </label>

              <input
                id='institutional-whatsapp'
                name='whatsapp'
                type='tel'
                required
                autoComplete='tel'
                className='w-full rounded-md border border-[#DDD8CC] bg-white px-4 py-3 text-[#4F4938] outline-none transition focus:border-[#00D866] focus:ring-2 focus:ring-[#00D866]/15'
              />
            </div>
          </div>

          <div>
            <label
              htmlFor='institutional-location'
              className='block font-sans text-sm font-medium text-[#7D744F] mb-2'
            >
              Localidad / Provincia *
            </label>

            <input
              id='institutional-location'
              name='location'
              type='text'
              required
              autoComplete='address-level2'
              className='w-full rounded-md border border-[#DDD8CC] bg-white px-4 py-3 text-[#4F4938] outline-none transition focus:border-[#00D866] focus:ring-2 focus:ring-[#00D866]/15'
            />
          </div>

          <div>
            <label
              htmlFor='institutional-reason'
              className='block font-sans text-sm font-medium text-[#7D744F] mb-2'
            >
              ¿En qué podemos ayudarte? *
            </label>

            <select
              id='institutional-reason'
              name='reason'
              required
              defaultValue=''
              className='w-full rounded-md border border-[#DDD8CC] bg-white px-4 py-3 text-[#4F4938] outline-none transition focus:border-[#00D866] focus:ring-2 focus:ring-[#00D866]/15'
            >
              <option value='' disabled>
                Seleccioná una opción
              </option>

              {reasons.map(reason => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor='institutional-message'
              className='block font-sans text-sm font-medium text-[#7D744F] mb-2'
            >
              Mensaje
              <span className='ml-1 font-normal text-[#9E9283]'>opcional</span>
            </label>

            <textarea
              id='institutional-message'
              name='message'
              rows={5}
              placeholder='Contanos qué materiales necesitás, cantidades o cualquier información que quieras agregar.'
              className='w-full resize-none rounded-md border border-[#DDD8CC] bg-white px-4 py-3 text-[#4F4938] placeholder:text-[#A9A393] outline-none transition focus:border-[#00D866] focus:ring-2 focus:ring-[#00D866]/15'
            />
          </div>

          {success && (
            <div
              role='status'
              className='rounded-md bg-[#F0FBEF] px-4 py-3 text-sm leading-relaxed text-[#5D684D]'
            >
              Gracias. Recibimos tu consulta y nos vamos a poner en contacto con
              vos.
            </div>
          )}

          {error && (
            <div
              role='alert'
              className='rounded-md bg-red-50 px-4 py-3 text-sm leading-relaxed text-red-700'
            >
              {error}
            </div>
          )}

          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2'>
            <Button
              type='submit'
              disabled={loading}
              className='w-full sm:w-auto sm:min-w-[220px] bg-[#FFA474] text-white px-9 py-3.5 text-sm font-semibold rounded-full hover:bg-[#e68c5c] transition-all disabled:opacity-60'
            >
              {loading ? 'Enviando...' : 'Enviar consulta'}
            </Button>

            <a
              href='https://wa.me/5493416901763'
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center justify-center sm:justify-end gap-2 font-sans text-sm font-medium text-[#9E9283] hover:text-[#7D744F] transition-colors'
            >
              <Phone className='h-4 w-4' />
              Consultar por WhatsApp
            </a>
          </div>
        </form>
      </div>
    </div>
  )
}
